/**
 * EpisodeOfCareTimeline Component
 * 
 * Visual timeline showing all activities for an oncology episode of care.
 * Shows encounters, treatments, assessments, and other key events.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Button,
  Grid,
  Container
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  LocalHospital as DiagnosisIcon,
  EventNote as EncounterIcon,
  MedicalServices as TreatmentIcon,
  Science as ChemoIcon,
  Warning as ToxicityIcon,
  Groups as TumorBoardIcon,
  Flag as StartIcon,
  CheckCircle as EndIcon,
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  Timeline as TimelineIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getEpisodeTimeline } from '../../services/episodeOfCareService';
import LinkedEncountersPanel from './LinkedEncountersPanel';
import { PageHeader, ContentCard, StatsCard, HeaderButton } from './oncologyStyles';

// Timeline event type configuration
const eventTypeConfig = {
  episode_start: {
    icon: <StartIcon />,
    color: 'primary',
    label: 'Episode Started'
  },
  episode_end: {
    icon: <EndIcon />,
    color: 'success',
    label: 'Episode Completed'
  },
  diagnosis: {
    icon: <DiagnosisIcon />,
    color: 'error',
    label: 'Diagnosis'
  },
  encounter: {
    icon: <EncounterIcon />,
    color: 'info',
    label: 'Encounter'
  },
  treatment_plan: {
    icon: <TreatmentIcon />,
    color: 'secondary',
    label: 'Treatment Plan'
  },
  chemo_cycle: {
    icon: <ChemoIcon />,
    color: 'warning',
    label: 'Chemotherapy'
  },
  toxicity: {
    icon: <ToxicityIcon />,
    color: 'error',
    label: 'Toxicity'
  },
  tumor_board: {
    icon: <TumorBoardIcon />,
    color: 'primary',
    label: 'Tumor Board'
  }
};

const EpisodeOfCareTimeline = ({
  episodeId,
  episode,
  onBack,
  onEventClick,
  showHeader = true,
  showLinkedEncounters = true
}) => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkedEncounterIds, setLinkedEncounterIds] = useState([]);

  // Fetch timeline
  const fetchTimeline = useCallback(async () => {
    if (!episodeId) return;

    try {
      setLoading(true);
      setError(null);
      const result = await getEpisodeTimeline(episodeId);
      setTimelineData(result);
      // Extract encounter IDs from timeline data
      setLinkedEncounterIds(result?.encounterIds || []);
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setError('Failed to load episode timeline');
    } finally {
      setLoading(false);
    }
  }, [episodeId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'h:mm a');
    } catch {
      return '';
    }
  };

  // Get event config
  const getEventConfig = (type) => {
    return eventTypeConfig[type] || {
      icon: <EncounterIcon />,
      color: 'grey',
      label: type
    };
  };

  // Handle event click
  const handleEventClick = (event) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, minHeight: 300 }}>
        <CircularProgress size={48} />
        <Typography sx={{ ml: 2 }} color="text.secondary">Loading timeline...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ m: 2, borderRadius: 2 }}
        action={
          <Button color="inherit" size="small" onClick={fetchTimeline}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!timelineData) {
    return null;
  }

  const { episodeName, status, currentPhase, timeline, patientId, patientName, patientMRN, diagnosisSummary } = timelineData;

  // Handle encounter link/unlink
  const handleEncounterLinked = (encounterId) => {
    setLinkedEncounterIds(prev => [...prev, encounterId]);
    // Refresh timeline to show updated data
    fetchTimeline();
  };

  const handleEncounterUnlinked = (encounterId) => {
    setLinkedEncounterIds(prev => prev.filter(id => id !== encounterId));
    // Refresh timeline to show updated data
    fetchTimeline();
  };

  // Get status chip color
  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case 'Active': return 'success';
      case 'Completed': return 'info';
      case 'On Hold': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  // Get phase chip color
  const getPhaseColor = (phaseValue) => {
    switch (phaseValue) {
      case 'Active Treatment': return 'primary';
      case 'Surveillance': return 'info';
      case 'Survivorship': return 'success';
      case 'Palliative': return 'warning';
      case 'End of Life': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header with gradient styling */}
      {showHeader && (
        <PageHeader
          title={episodeName || 'Episode Timeline'}
          subtitle={diagnosisSummary || 'Oncology Episode of Care'}
          icon={<TimelineIcon />}
          onBack={onBack}
          actions={
            <HeaderButton
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchTimeline}
            >
              Refresh
            </HeaderButton>
          }
        />
      )}

      {/* Patient Info & Status Bar */}
      <ContentCard sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PersonIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {patientName || 'Unknown Patient'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    MRN: {patientMRN || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap' }}>
                <Chip 
                  label={status || 'Unknown'} 
                  size="small" 
                  color={getStatusColor(status)}
                  sx={{ fontWeight: 600 }}
                />
                <Chip 
                  label={currentPhase || 'Unknown Phase'} 
                  size="small" 
                  color={getPhaseColor(currentPhase)}
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </ContentCard>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '10px', 
                bgcolor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1
              }}>
                <EncounterIcon sx={{ color: 'primary.main' }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {timeline?.filter(e => e.type === 'encounter').length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Encounters</Typography>
            </CardContent>
          </StatsCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '10px', 
                bgcolor: 'secondary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1
              }}>
                <TreatmentIcon sx={{ color: 'secondary.main' }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" color="secondary.main">
                {timeline?.filter(e => e.type === 'treatment_plan').length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Treatment Plans</Typography>
            </CardContent>
          </StatsCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '10px', 
                bgcolor: 'warning.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1
              }}>
                <ChemoIcon sx={{ color: 'warning.main' }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {timeline?.filter(e => e.type === 'chemo_cycle').length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Chemo Cycles</Typography>
            </CardContent>
          </StatsCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ 
                width: 40, 
                height: 40, 
                borderRadius: '10px', 
                bgcolor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1
              }}>
                <ToxicityIcon sx={{ color: 'error.main' }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {timeline?.filter(e => e.type === 'toxicity').length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Toxicities</Typography>
            </CardContent>
          </StatsCard>
        </Grid>
      </Grid>

      {/* Linked Encounters Panel */}
      {showLinkedEncounters && (
        <LinkedEncountersPanel
          episodeId={episodeId}
          patientId={patientId}
          linkedEncounterIds={linkedEncounterIds}
          onEncounterLinked={handleEncounterLinked}
          onEncounterUnlinked={handleEncounterUnlinked}
          readOnly={status === 'Completed' || status === 'Cancelled'}
        />
      )}

      {/* Timeline Section */}
      <ContentCard>
        <CardContent sx={{ p: 0 }}>
          {/* Section Header */}
          <Box sx={{ 
            px: 3, 
            py: 2, 
            borderBottom: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
          }}>
            <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon color="primary" />
              Care Timeline
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Chronological view of all clinical events
            </Typography>
          </Box>

          {/* Timeline Content */}
          {!timeline || timeline.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2
              }}>
                <CalendarIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
              </Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Timeline Events Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Events will appear here as the patient's care journey progresses.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Timeline position="alternate">
                {timeline.map((event, index) => {
                  const config = getEventConfig(event.type);
                  const isFirst = index === 0;
                  const isLast = index === timeline.length - 1;

                  return (
                    <TimelineItem key={`${event.type}-${index}`}>
                      <TimelineOppositeContent
                        sx={{ 
                          m: 'auto 0',
                          flex: 0.3
                        }}
                        variant="body2"
                        color="text.secondary"
                      >
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {formatDate(event.date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(event.date)}
                        </Typography>
                      </TimelineOppositeContent>
                      <TimelineSeparator>
                        {!isFirst && <TimelineConnector sx={{ bgcolor: 'primary.light' }} />}
                        <Tooltip title={config.label} arrow>
                          <TimelineDot 
                            color={config.color}
                            sx={{ 
                              cursor: 'pointer',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'scale(1.15)'
                              }
                            }}
                            onClick={() => handleEventClick(event)}
                          >
                            {config.icon}
                          </TimelineDot>
                        </Tooltip>
                        {!isLast && <TimelineConnector sx={{ bgcolor: 'primary.light' }} />}
                      </TimelineSeparator>
                      <TimelineContent sx={{ py: '12px', px: 2 }}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            cursor: onEventClick ? 'pointer' : 'default',
                            borderRadius: 2,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': onEventClick ? { 
                              borderColor: 'primary.main',
                              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                              transform: 'translateY(-2px)'
                            } : {}
                          }}
                          onClick={() => handleEventClick(event)}
                        >
                          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                              <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                {event.title}
                              </Typography>
                              {event.status && (
                                <Chip 
                                  label={event.status} 
                                  size="small" 
                                  color={
                                    event.status === 'Completed' ? 'success' :
                                    event.status === 'Active' ? 'info' :
                                    event.status === 'Resolved' ? 'success' :
                                    'default'
                                  }
                                  variant="outlined"
                                  sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                            {event.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                                {event.description.length > 100 
                                  ? `${event.description.substring(0, 100)}...` 
                                  : event.description}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </TimelineContent>
                    </TimelineItem>
                  );
                })}
              </Timeline>
            </Box>
          )}
        </CardContent>
      </ContentCard>
    </Box>
  );
};

export default EpisodeOfCareTimeline;
